/**
 * Search parser with AND/OR boolean operator support.
 * 
 * Comma (,) = OR operator — separates independent groups
 * Plus (+)  = AND operator — all sub-terms within a group must match
 * 
 * Example: "fish + chip, cabbage, fries"
 *   → OR groups: ["fish + chip", "cabbage", "fries"]
 *   → Parsed: [[fish, chip], [cabbage], [fries]]
 *   → Matches items where (name contains "fish" AND "chip") OR "cabbage" OR "fries"
 * 
 * Quoted terms use double quotes for exact name matching.
 * Prefix operators: # (category), @ (location), ~ (description)
 */

export interface ParsedTerm {
  raw: string;      // Original term string (with quotes if exact)
  term: string;     // Cleaned lowercase term
  isExact: boolean; // Whether quoted for exact match
}

/** An OR group is an array of AND terms */
export type SearchGroup = ParsedTerm[];

/**
 * Parse a single segment (no commas) into terms split by +.
 * Preserves quoted phrases.
 */
function parseSegment(segment: string): ParsedTerm[] {
  const terms: ParsedTerm[] = [];
  // Split by + but preserve quoted phrases
  // First extract quoted phrases, then split remainder by +
  const normalized = segment.trim();
  if (!normalized) return terms;

  // Check if this segment contains + (AND operator)
  // Split by + that's not inside quotes
  const parts: string[] = [];
  let current = '';
  let inQuote = false;
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    if (ch === '"') {
      inQuote = !inQuote;
      current += ch;
    } else if (ch === '+' && !inQuote) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  parts.push(current);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    // Check if it's a quoted term
    const quoteMatch = trimmed.match(/^"([^"]+)"$/);
    if (quoteMatch) {
      terms.push({
        raw: trimmed,
        term: quoteMatch[1].toLowerCase(),
        isExact: true,
      });
    } else {
      terms.push({
        raw: trimmed,
        term: trimmed.toLowerCase(),
        isExact: false,
      });
    }
  }

  return terms;
}

/**
 * Parse a full search string into OR groups of AND terms.
 * 
 * Comma separates OR groups; + separates AND terms within a group.
 * Returns SearchGroup[] where the item matches if ANY group matches,
 * and a group matches if ALL its terms match.
 */
export function parseSearchGroups(searchString: string): SearchGroup[] {
  if (!searchString.trim()) return [];

  // Normalize smart/curly quotes
  const normalized = searchString.replace(/[\u201C\u201D\u2018\u2019]/g, '"');

  // Split by comma (OR), but preserve quoted phrases
  const orSegments: string[] = [];
  let current = '';
  let inQuote = false;
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    if (ch === '"') {
      inQuote = !inQuote;
      current += ch;
    } else if (ch === ',' && !inQuote) {
      orSegments.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  orSegments.push(current);

  const groups: SearchGroup[] = [];
  for (const segment of orSegments) {
    const terms = parseSegment(segment);
    if (terms.length > 0) {
      groups.push(terms);
    }
  }

  return groups;
}

/**
 * Flatten search groups into a flat list of raw terms (for backward-compat uses).
 * Each OR group's AND terms are kept as separate entries.
 */
export function flattenSearchGroups(groups: SearchGroup[]): string[] {
  const result: string[] = [];
  for (const group of groups) {
    for (const term of group) {
      result.push(term.raw);
    }
  }
  return result;
}

/**
 * Match a single parsed term against an item.
 * This is the single source of truth for search term matching logic.
 * 
 * @param pt - The parsed term to match
 * @param item - The item to match against (must have name, categories, area, location, sub_location, description, id)
 * @param voiceMode - If true, uses word-boundary matching for plain name terms
 */
export function matchTermToItem(pt: ParsedTerm, item: any, voiceMode = false): boolean {
  const { term, isExact } = pt;
  
  // Helper function to normalize names for comparison
  const normalizeForMatch = (name: string): string => {
    return name.toLowerCase()
      .replace(/\b\d+(?:\.\d+)?\s*(?:kg|g|lb|oz|lbs|grams?|ounces?|pounds?)\b/g, '')
      .replace(/\b(weighing|approximately|about|around|roughly)\b/g, '')
      .replace(/ies\b/g, 'y')
      .replace(/s\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  // Special case: if this looks like a recipe ingredient (has quantities or measurements)
  // Use normalization for matching
  if (/\d|kg|g|lb|oz|weighing/i.test(term)) {
    const normalizedTerm = normalizeForMatch(term);
    const normalizedItemName = normalizeForMatch(item.name || '');
    
    if (isExact) {
      return normalizedItemName === normalizedTerm;
    }
    return normalizedItemName.includes(normalizedTerm);
  }

  // # prefix: match categories or hashtags in description, or item ID
  if (term.startsWith('#')) {
    const hashQuery = term.slice(1);
    if (!hashQuery) return false;
    const idMatch = hashQuery.match(/^(\d+)(\s|$)/);
    if (idMatch) {
      return item.id === parseInt(idMatch[1], 10);
    }
    const catLabels = item.categories ? item.categories.map((cat: any) => cat.label.toLowerCase()) : [];
    if (catLabels.some((label: string) => label.includes(hashQuery))) return true;
    const hashtags = (item.description || '').toLowerCase().match(/#[\w-]+/g) || [];
    return hashtags.some((tag: string) => tag.slice(1).includes(hashQuery));
  }

  // @ prefix: match location fields (area, location, sub_location)
  if (term.startsWith('@')) {
    const locQuery = term.slice(1);
    if (!locQuery) return false;
    const locFields = [
      (item.area || '').toLowerCase(),
      (item.location || '').toLowerCase(),
      (item.sub_location || '').toLowerCase(),
    ];
    return locFields.some(f => f.includes(locQuery));
  }

  // ~ prefix: match description (without hashtags)
  if (term.startsWith('~')) {
    const descQuery = term.slice(1);
    if (!descQuery) return false;
    const desc = (item.description || '').toLowerCase().replace(/#[\w-]+/g, '').trim();
    return desc.includes(descQuery);
  }

  // Quoted exact match: name only
  if (isExact) {
    return (item.name || '').toLowerCase().trim() === term;
  }

  // Default (no prefix): name match
  // Voice mode: whole-word match (so "cat" doesn't match "location")
  // Normal mode: substring match (for incremental typing)
  // Also handle simple plurals for better matching
  const itemName = (item.name || '').toLowerCase();
  const normalizedTerm = term.replace(/ies$/, 'y').replace(/s$/, '');
  const normalizedItemName = itemName.replace(/ies$/, 'y').replace(/s$/, '');
  
  if (voiceMode) {
    const escaped = normalizedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(normalizedItemName);
  }
  return normalizedItemName.includes(normalizedTerm) || itemName.includes(term);
}

/**
 * Test whether an item matches the given search groups.
 * OR across groups, AND within each group.
 */
export function itemMatchesSearch(groups: SearchGroup[], item: any, voiceMode = false): boolean {
  if (groups.length === 0) return true;
  return groups.some(group => group.every(pt => matchTermToItem(pt, item, voiceMode)));
}

/**
 * Check if a search string contains any AND operators (+).
 */
export function hasAndOperator(searchString: string): boolean {
  // Check for + not inside quotes
  let inQuote = false;
  for (let i = 0; i < searchString.length; i++) {
    const ch = searchString[i];
    if (ch === '"') inQuote = !inQuote;
    else if (ch === '+' && !inQuote) return true;
  }
  return false;
}

/**
 * Convert all separators in a search string between , and +.
 * Used by the AND/OR toggle.
 */
export function toggleSearchOperator(searchString: string, toAnd: boolean): string {
  if (!searchString.trim()) return searchString;

  const normalized = searchString.replace(/[\u201C\u201D\u2018\u2019]/g, '"');
  let result = '';
  let inQuote = false;

  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    if (ch === '"') {
      inQuote = !inQuote;
      result += ch;
    } else if (!inQuote) {
      if (toAnd && ch === ',') {
        // Replace comma with +, preserve surrounding whitespace
        result += '+';
      } else if (!toAnd && ch === '+') {
        // Replace + with comma
        result += ',';
      } else {
        result += ch;
      }
    } else {
      result += ch;
    }
  }

  return result;
}
